import { createResource, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { api, type RequestTraceDetail, type RequestTraceSource, type RequestTraceTriggerSource } from "../shared";
import { Pagination } from "./Pagination";
import { PerformanceTraceTimelineView } from "./PerformanceTraceTimelineView";

function parseSelectedTraceId() {
  const hash = window.location.hash.replace(/^#/, "");
  const match = /^performance\/trace\/(.+)$/.exec(hash);
  return match?.[1] ?? null;
}

function parseRequestIds(value: string) {
  return Array.from(
    new Set(
      value
        .split(/[\n,]+/)
        .map((entry) => entry.trim())
        .filter(Boolean),
    ),
  );
}

export function PerformancePanel() {
  const [requestIdsInput, setRequestIdsInput] = createSignal("");
  const [source, setSource] = createSignal<RequestTraceSource | "">("");
  const [method, setMethod] = createSignal("");
  const [path, setPath] = createSignal("");
  const [statusCode, setStatusCode] = createSignal("");
  const [triggerSource, setTriggerSource] = createSignal<RequestTraceTriggerSource | "">("");
  const [minDurationMs, setMinDurationMs] = createSignal("");
  const [organizationId, setOrganizationId] = createSignal("");
  const [projectId, setProjectId] = createSignal("");
  const [page, setPage] = createSignal(1);
  const [perPage, setPerPage] = createSignal(10);
  const [selectedId, setSelectedId] = createSignal<string | null>(parseSelectedTraceId());
  const [pendingDeleteId, setPendingDeleteId] = createSignal<string | null>(null);
  const [pendingClearAll, setPendingClearAll] = createSignal(false);
  const [filters, setFilters] = createSignal<Record<string, any>>({});

  const [traces, { refetch }] = createResource(
    () => ({ ...filters(), page: page(), perPage: perPage() }),
    (filters) => api.performance.traces(filters),
  );

  const [selectedTrace] = createResource(
    selectedId,
    (id) => id ? api.performance.trace(id) : Promise.resolve(null as RequestTraceDetail | null),
  );

  onMount(() => {
    const syncHash = () => setSelectedId(parseSelectedTraceId());
    window.addEventListener("hashchange", syncHash);
    onCleanup(() => window.removeEventListener("hashchange", syncHash));
  });

  function applyFilters(e: Event) {
    e.preventDefault();
    setPage(1);
    setFilters({
      requestIds: parseRequestIds(requestIdsInput()),
      source: source() || undefined,
      method: method().trim() || undefined,
      path: path().trim() || undefined,
      statusCode: statusCode().trim() ? Number(statusCode()) : undefined,
      triggerSource: triggerSource() || undefined,
      minDurationMs: minDurationMs().trim() ? Number(minDurationMs()) : undefined,
      organizationId: organizationId().trim() || undefined,
      projectId: projectId().trim() || undefined,
    });
  }

  function openTrace(id: string) {
    setSelectedId(id);
    setPendingDeleteId(null);
    window.location.hash = `performance/trace/${id}`;
  }

  function closeTrace() {
    setSelectedId(null);
    setPendingDeleteId(null);
    window.location.hash = "performance";
  }

  function openTraceInNewWindow(id: string) {
    window.open(`${window.location.pathname}${window.location.search}#performance/trace/${id}`, "_blank", "noopener,noreferrer");
  }

  async function deleteTrace(id: string) {
    if (pendingDeleteId() !== id) {
      setPendingDeleteId(id);
      return;
    }
    await api.performance.deleteTrace(id);
    setPendingDeleteId(null);
    if (selectedId() === id) {
      closeTrace();
    }
    await refetch();
  }

  async function clearAllTraces() {
    if (!pendingClearAll()) {
      setPendingClearAll(true);
      return;
    }
    await api.performance.clearTraces();
    setPendingClearAll(false);
    setPendingDeleteId(null);
    if (selectedId()) {
      closeTrace();
    }
    await refetch();
  }

  return (
    <Show
      when={selectedId()}
      fallback={
        <div class="space-y-4">
          <div>
            <h2 class="text-lg font-semibold text-gray-100">Performance Traces</h2>
            <p class="text-sm text-gray-400">Inspect opt-in request traces with DB, cache, and custom span timings.</p>
          </div>

          <form onSubmit={applyFilters} class="grid gap-3 rounded-xl border border-gray-800 bg-gray-900/40 p-4 md:grid-cols-6">
            <label class="space-y-1 text-sm text-gray-300">
              <span>Source</span>
              <select value={source()} onChange={(e) => setSource(e.currentTarget.value as RequestTraceSource | "")} class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100">
                <option value="">All</option>
                <option value="api">API</option>
                <option value="system">System</option>
              </select>
            </label>
            <label class="space-y-1 text-sm text-gray-300">
              <span>Method</span>
              <input value={method()} onInput={(e) => setMethod(e.currentTarget.value.toUpperCase())} placeholder="GET" class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100" />
            </label>
            <label class="space-y-1 text-sm text-gray-300">
              <span>Status</span>
              <input value={statusCode()} onInput={(e) => setStatusCode(e.currentTarget.value)} placeholder="200" class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100" />
            </label>
            <label class="space-y-1 text-sm text-gray-300">
              <span>Trigger</span>
              <select value={triggerSource()} onChange={(e) => setTriggerSource(e.currentTarget.value as RequestTraceTriggerSource | "")} class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100">
                <option value="">All</option>
                <option value="header">Header</option>
                <option value="cookie">Cookie</option>
              </select>
            </label>
            <label class="space-y-1 text-sm text-gray-300">
              <span>Min Duration (ms)</span>
              <input value={minDurationMs()} onInput={(e) => setMinDurationMs(e.currentTarget.value)} placeholder="50" class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100" />
            </label>
            <label class="space-y-1 text-sm text-gray-300 md:col-span-3">
              <span>Organization ID</span>
              <input value={organizationId()} onInput={(e) => setOrganizationId(e.currentTarget.value)} placeholder="org id" class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100" />
            </label>
            <label class="space-y-1 text-sm text-gray-300 md:col-span-3">
              <span>Project ID</span>
              <input value={projectId()} onInput={(e) => setProjectId(e.currentTarget.value)} placeholder="project id" class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100" />
            </label>
            <label class="space-y-1 text-sm text-gray-300 md:col-span-6">
              <span>Request IDs</span>
              <textarea
                value={requestIdsInput()}
                onInput={(e) => setRequestIdsInput(e.currentTarget.value)}
                placeholder={"trace-id-1,\ntrace-id-2"}
                rows="3"
                class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100"
              />
            </label>
            <label class="space-y-1 text-sm text-gray-300 md:col-span-6">
              <span>Path</span>
              <input value={path()} onInput={(e) => setPath(e.currentTarget.value)} placeholder="/api/v1/projects/..." class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100" />
            </label>
            <div class="flex items-end gap-2 md:col-span-6">
              <button type="submit" class="rounded bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-500">Apply</button>
              <button type="button" onClick={() => refetch()} class="rounded border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800">Refresh</button>
              <button type="button" onClick={() => void clearAllTraces()} class="rounded border border-red-700 px-4 py-2 text-sm text-red-200 hover:bg-red-950/50">{pendingClearAll() ? "Confirm Delete All" : "Delete All Traces"}</button>
              <Show when={pendingClearAll()}>
                <button type="button" onClick={() => setPendingClearAll(false)} class="rounded border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800">Cancel</button>
              </Show>
            </div>
          </form>

          <section class="overflow-hidden rounded-xl border border-gray-800 bg-gray-900/40">
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-800 text-sm">
                <thead class="bg-gray-950/60 text-left text-xs uppercase tracking-wide text-gray-400">
                  <tr>
                    <th class="px-4 py-3">Started</th>
                    <th class="px-4 py-3">Source</th>
                    <th class="px-4 py-3">Method</th>
                    <th class="px-4 py-3">Path</th>
                    <th class="px-4 py-3">Status</th>
                    <th class="px-4 py-3">Duration</th>
                    <th class="px-4 py-3">Spans</th>
                    <th class="px-4 py-3">Visual</th>
                    <th class="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-800">
                  <Show when={!traces.loading} fallback={<tr><td colSpan="9" class="px-4 py-6 text-center text-gray-500">Loading traces...</td></tr>}>
                    <For each={traces()?.items ?? []}>
                      {(entry) => (
                        <tr
                          class="cursor-pointer transition-colors hover:bg-gray-800/50"
                          onClick={() => openTrace(entry.id)}
                        >
                          <td class="px-4 py-3 text-gray-300">{new Date(entry.requestStartedAt).toLocaleString()}</td>
                          <td class="px-4 py-3 text-gray-300">{entry.source}</td>
                          <td class="px-4 py-3 text-gray-300">{entry.method}</td>
                          <td class="max-w-xs truncate px-4 py-3 text-gray-300">{entry.path}</td>
                          <td class="px-4 py-3 text-gray-300">{entry.statusCode ?? "-"}</td>
                          <td class="px-4 py-3 text-gray-300">{entry.durationMs} ms</td>
                          <td class="px-4 py-3 text-gray-300">{entry.spanCount}</td>
                          <td class="px-4 py-3">
                            <div class="h-3 w-28 rounded bg-gray-950/80">
                              <div class={`h-3 rounded ${entry.statusCode && entry.statusCode >= 500 ? "bg-red-500/80" : entry.durationMs >= 100 ? "bg-amber-500/80" : "bg-cyan-500/80"}`} style={{ width: `${Math.min(100, Math.max(8, entry.durationMs / 5))}%` }} />
                            </div>
                          </td>
                          <td class="px-4 py-3">
                            <div class="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openTraceInNewWindow(entry.id);
                                }}
                                class="rounded border border-gray-700 px-3 py-1.5 text-xs text-gray-200 hover:bg-gray-800"
                              >
                                Open Window
                              </button>
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void deleteTrace(entry.id);
                                }}
                                class="rounded border border-red-700 px-3 py-1.5 text-xs text-red-200 hover:bg-red-950/50"
                              >
                                {pendingDeleteId() === entry.id ? "Confirm Delete" : "Delete"}
                              </button>
                              <Show when={pendingDeleteId() === entry.id}>
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setPendingDeleteId(null);
                                  }}
                                  class="rounded border border-gray-700 px-3 py-1.5 text-xs text-gray-200 hover:bg-gray-800"
                                >
                                  Cancel
                                </button>
                              </Show>
                            </div>
                          </td>
                        </tr>
                      )}
                    </For>
                  </Show>
                </tbody>
              </table>
            </div>
            <div class="px-4 pb-4">
              <Pagination
                page={traces()?.page ?? page()}
                totalPages={traces()?.totalPages ?? 1}
                perPage={traces()?.perPage ?? perPage()}
                total={traces()?.total ?? 0}
                loading={traces.loading}
                onPageChange={(nextPage) => setPage(nextPage)}
                onPerPageChange={(nextPerPage) => {
                  setPerPage(nextPerPage);
                  setPage(1);
                }}
              />
            </div>
          </section>
        </div>
      }
    >
      {() => (
        <Show
          when={selectedTrace()}
          fallback={<div class="rounded-xl border border-gray-800 bg-gray-900/40 p-6 text-sm text-gray-400">{selectedTrace.loading ? "Loading trace timeline..." : "Request trace not found."}</div>}
        >
          {(detail) => (
            <PerformanceTraceTimelineView
              detail={detail()}
              onBack={closeTrace}
              onOpenWindow={() => {
                const id = selectedId();
                if (id) openTraceInNewWindow(id);
              }}
              onDeleteTrace={async () => {
                const id = selectedId();
                if (id) {
                  await deleteTrace(id);
                }
              }}
              deletePending={pendingDeleteId() === selectedId()}
              onDeleteCancel={() => setPendingDeleteId(null)}
            />
          )}
        </Show>
      )}
    </Show>
  );
}
